import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { StatusBadge, ReceivableStatusBadge } from "../components/etams/StatusBadge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { CreditCard, XCircle, Wallet } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDateTime } from "../lib/format";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import ProofUploader from "../components/etams/ProofUploader";
import { Link } from "react-router-dom";

// Kolom ekspor CSV — yang dibaca orang keuangan di Excel, bukan yang dibaca kode.
const KOLOM_CSV = [
  { kunci: "fa_number", judul: "Nomor FA" },
  { kunci: "outstanding_amount_idr", judul: "Outstanding (IDR)" },
  { kunci: "initial_outstanding_amount_idr", judul: "Nilai Awal (IDR)" },
  { kunci: "due_date", judul: "Jatuh Tempo" },
  { kunci: "status_api", judul: "Status" },
  { kunci: "created_at", judul: "Dibuat", ambil: (r) => formatDateTime(r.created_at) },
];

// Peta status pindah ke `RECEIVABLE_STATUS_TONE` di components/etams/StatusBadge
// (KONTRAS-01 Batch 10) — label tetap sama, warnanya kini dari token tema.

const BUKTI_KOSONG = { file_url: "", bank_from: "", bank_to: "", reference_number: "", notes: "" };

const daysOverdue = (due_date) => {
  if (!due_date) return null;
  const d = new Date(due_date);
  const now = new Date();
  const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : null;
};

export default function Receivables() {
  const { isOwner, roleCode } = useAuth();
  // Langkah 25 KPI follow-up (Q85): penagih = peran pelunas (backend RECEIVABLE_SETTLE_ROLES).
  const bolehFollowUp = ["OWNER", "KEPALA_CABANG", "CS"].includes(roleCode);
  const [items, setItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterOverdue, setFilterOverdue] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [settling, setSettling] = useState(null);
  const [settleAmount, setSettleAmount] = useState("");
  const [bukti, setBukti] = useState(BUKTI_KOSONG);
  const [caraBayar, setCaraBayar] = useState("TRANSFER");
  // Yang mengajukan pelunasan = CS (bukti dari CS, dikonfirmasi KC — BUSINESS-78).
  const bolehAjukan = roleCode === "CS";
  const bolehKonfirmasi = ["OWNER", "KEPALA_CABANG"].includes(roleCode);
  const [writingOffId, setWritingOffId] = useState(null);

  // Total dari SERVER — jumlah yang berhak dilihat pengguna ini, bukan jumlah
  // baris di halaman. Sebelum 2 Sep 2026 layar ini nol punya angka itu, dan
  // endpoint-nya memotong senyap di 100.
  const [total, setTotal] = useState(0);
  // `urutSah` WAJIB cermin `URUT_SAH` di routers/receivables.py. Kolom di
  // luarnya (fa_number, status_api) medan hasil pengayaan/hitungan — server
  // nol bisa mengurutkannya, jadi kepalanya nol boleh jadi tombol.
  const daftar = useDaftar(items, {
    server: { total, urutSah: ["created_at", "due_date", "outstanding_amount_idr", "status"] },
    urutAwal: { kunci: "due_date", arah: "asc" },
  });
  const { cari: q, limit, offset, sort, arah } = daftar.paramServer;

  const bangunParams = useCallback((ovr = {}) => {
    const p = { limit, offset, sort, arah, ...ovr };
    if (filterStatus !== "ALL") p.status = filterStatus;
    if (filterOverdue) p.lewat_tempo = true;
    if (q) p.cari = q;
    return p;
  }, [filterStatus, filterOverdue, q, limit, offset, sort, arah]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/receivables", { params: bangunParams() });
      setItems(r.data.items || []);
      setTotal(typeof r.data.total === "number" ? r.data.total : (r.data.items || []).length);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Gagal memuat"); }
    finally { setLoading(false); }
  }, [bangunParams]);
  useEffect(() => { load(); }, [load]);

  // Ekspor menempuh SELURUH halaman, bukan yang sedang tampil — lihat
  // `eksporCsvServer`. Penyaring aktif ikut; yang diekspor adalah apa yang
  // dilihat pengguna, bukan seluruh tabel.
  const ambilHalaman = useCallback(async (off, n) => {
    const r = await api.get("/receivables", { params: bangunParams({ offset: off, limit: n }) });
    return r.data;
  }, [bangunParams]);

  // K212 / BUSINESS-78 (Owner 12 Okt 2026): pelunasan piutang = pembayaran biasa. CS mengunggah bukti di sini
  // (PELUNASAN atas FA-nya), Kepala Cabang mengonfirmasi di halaman FA; piutang berkurang/tertutup otomatis saat
  // pembayaran dikonfirmasi. Tombol lama "Catat pelunasan" (tanpa bukti, tanpa penyetuju) sudah dicabut.
  const submitSettle = async () => {
    const amt = parseInt(settleAmount);
    if (!amt || amt <= 0) { toast.error("Nominal pelunasan belum diisi"); return; }
    if (amt > settling.outstanding_amount_idr) { toast.error(`Nominal melebihi sisa piutang ${formatIDR(settling.outstanding_amount_idr)}`); return; }
    if (!bukti.file_url) { toast.error("Unggah bukti pembayaran dulu"); return; }
    try {
      await api.post("/payments/submit", {
        payment_type: "PELUNASAN", payment_method: caraBayar, amount_idr: amt, fa_id: settling.fa_id,
        proof_category: caraBayar === "CASH" ? "CASH_PELUNASAN" : "TRANSFER_PELUNASAN",
        proof: { ...bukti },
      });
      toast.success("Pelunasan diajukan — menunggu konfirmasi Kepala Cabang");
      tutupAjukan(); load();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Pelunasan belum bisa diajukan"); }
  };
  const tutupAjukan = () => { setSettling(null); setSettleAmount(""); setBukti(BUKTI_KOSONG); setCaraBayar("TRANSFER"); };
  const tandaiFollowUp = async (r) => {
    try { await api.post(`/receivables/${r.id}/follow-up`); toast.success("Follow-up dicatat"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Gagal"); }
  };
  const submitWriteOff = async () => {
    try { await api.post(`/receivables/${writingOffId}/write-off`); toast.success("Piutang dihapus-buku"); setWritingOffId(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Gagal"); }
  };

  return (
    <div className="space-y-6" data-testid="receivables-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Wallet size={22} /> Piutang</h1>
          <p className="text-sm text-muted-foreground mt-1">Piutang untuk Special Customer yang barangnya diserahkan sebelum lunas. Wajib persetujuan Owner yang tercatat.</p>
        </div>
      </div>
      <Card className="p-4">
        <PalangDaftar daftar={daftar} placeholder="Cari nomor FA…" testId="rec-palang"
                      ekspor={{ nama: "piutang", kolom: KOLOM_CSV, ambilHalaman }}>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48 h-9" data-testid="rec-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Status</SelectItem>
              <SelectItem value="PENDING">Terbuka / Sebagian</SelectItem>
              <SelectItem value="OVERDUE">Lewat Tempo</SelectItem>
              <SelectItem value="PAID">Lunas</SelectItem>
              <SelectItem value="WRITTEN_OFF">Dihapus-Buku</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={filterOverdue} onChange={(e) => setFilterOverdue(e.target.checked)} data-testid="rec-filter-overdue" />
            Hanya yang lewat tempo
          </label>
        </PalangDaftar>
        {loading ? <div className="py-8 text-center text-sm text-muted-foreground">Memuat...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <tr>
                  <ThUrut daftar={daftar} kunci="fa_number" className="text-left px-3 py-2">Nomor FA</ThUrut>
                  <ThUrut daftar={daftar} kunci="outstanding_amount_idr" className="text-right px-3 py-2" align="right">Outstanding</ThUrut>
                  <ThUrut daftar={daftar} kunci="due_date" className="text-left px-3 py-2">Jatuh Tempo</ThUrut>
                  <th className="text-center px-3 py-2">Overdue</th>
                  {/* Diurut kolom `status` yang nyata; sel menampilkan
                      `status_api` turunannya (OPEN/PARTIAL sama-sama lahir
                      dari PENDING). */}
                  <ThUrut daftar={daftar} kunci="status" className="text-left px-3 py-2">Status</ThUrut>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {daftar.total === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  {daftar.cari.trim() ? "Tidak ada yang cocok dengan pencarian." : "Tidak ada piutang."}
                </td></tr>}
                {daftar.tampil.map((r) => {
                  const od = daysOverdue(r.due_date);
                  const isOpen = r.status_api === "OPEN" || r.status_api === "PARTIAL" || r.status_api === "OVERDUE";
                  return (
                    <tr key={r.id} className="border-t border-border" data-testid={`rec-row-${r.id}`}>
                      {/* UAT7-T3 (b) — nomor FA, bukan UUID: orang keuangan
                          mencocokkan baris dengan dokumen fisik lewat NOMOR,
                          bukan fa_id. `fa_id` TETAP dikirim backend (dan tetap
                          ada di `r`) untuk kunci/audit, cuma tidak dirender.
                          Fallback "—" ikut konvensi `FAList.jsx` (fa_number
                          bisa kosong pada baris yang belum lengkap datanya). */}
                      <td className="px-3 py-2 font-medium">{r.fa_number || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatIDR(r.outstanding_amount_idr)}</td>
                      <td className="px-3 py-2 text-xs">{r.due_date || "—"}</td>
                      <td className="px-3 py-2 text-center text-xs">{od !== null ? <StatusBadge tone="danger">{od} hari</StatusBadge> : "—"}</td>
                      <td className="px-3 py-2"><ReceivableStatusBadge value={r.status_api} /></td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => setDetail(r)} data-testid={`rec-detail-${r.id}`}>Detail</Button>
                        {isOpen && r.pelunasan_menunggu && (
                          bolehKonfirmasi
                            ? <Link to={`/fa-orders/${r.fa_id}`} className="text-xs underline mr-2" data-testid={`rec-konfirmasi-${r.id}`}>Konfirmasi pelunasan {formatIDR(r.pelunasan_menunggu.amount_idr)}</Link>
                            : <span className="mr-2"><StatusBadge tone="warning">Menunggu konfirmasi {formatIDR(r.pelunasan_menunggu.amount_idr)}</StatusBadge></span>
                        )}
                        {isOpen && bolehAjukan && !r.pelunasan_menunggu && (
                          <Button variant="ghost" size="sm" onClick={() => { setSettling(r); setSettleAmount(String(r.outstanding_amount_idr)); }} className="text-success-soft-foreground" data-testid={`rec-settle-${r.id}`}><CreditCard size={12} className="mr-1" />Ajukan Pelunasan</Button>
                        )}
                        {bolehFollowUp && isOpen && (
                          <Button variant="ghost" size="sm" onClick={() => tandaiFollowUp(r)} title={r.followed_up_at ? `Follow-up terakhir ${r.followed_up_at.slice(0, 16).replace("T", " ")}` : "Belum pernah di-follow-up"} data-testid={`rec-followup-${r.id}`}>
                            {r.followed_up_at ? "Follow-up lagi" : "Tandai follow-up"}
                          </Button>
                        )}
                        {isOwner && isOpen && (
                          <Button variant="ghost" size="sm" onClick={() => setWritingOffId(r.id)} className="text-muted-foreground" data-testid={`rec-writeoff-${r.id}`}><XCircle size={12} className="mr-1" />Hapus-Buku</Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="rec-paginasi" />
          </div>
        )}
      </Card>

      {detail && (
        <Dialog open onOpenChange={(o) => { if (!o) setDetail(null); }}>
          <DialogContent data-testid="rec-detail-modal">
            <DialogHeader><DialogTitle>Detail Piutang</DialogTitle></DialogHeader>
            <div className="space-y-2 text-sm">
              <div><strong>Nomor FA:</strong> {detail.fa_number || "—"}</div>
              <div><strong>Nilai Awal:</strong> {formatIDR(detail.initial_outstanding_amount_idr)}</div>
              <div><strong>Outstanding Sekarang:</strong> <strong className="text-danger-soft-foreground">{formatIDR(detail.outstanding_amount_idr)}</strong></div>
              <div><strong>Jatuh Tempo:</strong> {detail.due_date || "—"}</div>
              <div><strong>Status:</strong> <ReceivableStatusBadge value={detail.status_api} /></div>
              <div><strong>Alasan:</strong> {detail.reason || "—"}</div>
              {isOwner && <div className="text-xs text-muted-foreground"><strong>Catatan persetujuan Owner:</strong> <code>{detail.owner_approval_audit_id}</code></div>}
              <div className="text-xs text-muted-foreground">Dibuat: {formatDateTime(detail.created_at)}</div>
              {detail.paid_at && <div className="text-xs text-muted-foreground">Lunas: {formatDateTime(detail.paid_at)}</div>}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setDetail(null)}>Tutup</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!settling} onOpenChange={(o) => { if (!o) tutupAjukan(); }}>
        <DialogContent data-testid="rec-settle-modal">
          <DialogHeader><DialogTitle>Ajukan Pelunasan Piutang</DialogTitle></DialogHeader>
          {settling && (
            <div className="space-y-3 py-2">
              <div className="text-sm">FA {settling.fa_number || "—"} · sisa piutang <strong>{formatIDR(settling.outstanding_amount_idr)}</strong></div>
              <div className="text-xs text-muted-foreground">
                Pelunasan dicatat sebagai pembayaran biasa: unggah bukti dari customer, lalu Kepala Cabang
                mengonfirmasinya di halaman FA. Piutang tertutup otomatis begitu pembayaran dikonfirmasi.
              </div>
              <ProofUploader testIdPrefix="rec-settle-upload" onUploaded={(r) => setBukti((b) => ({ ...b, file_url: r.file_url }))} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Cara bayar *</Label>
                  <Select value={caraBayar} onValueChange={setCaraBayar}>
                    <SelectTrigger className="h-9" data-testid="rec-settle-cara"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRANSFER">Transfer</SelectItem>
                      <SelectItem value="CASH">Tunai</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nominal dibayar (Rp) *</Label>
                  <Input inputMode="numeric" value={settleAmount} onChange={(e) => setSettleAmount(e.target.value.replace(/[^0-9]/g, ""))} data-testid="rec-settle-amount" />
                </div>
                <Input placeholder="Bank pengirim" value={bukti.bank_from} onChange={(e) => setBukti({ ...bukti, bank_from: e.target.value })} />
                <Input placeholder="No. referensi" value={bukti.reference_number} onChange={(e) => setBukti({ ...bukti, reference_number: e.target.value })} />
              </div>
              <div className="text-xs text-muted-foreground">
                Nominal di bawah sisa wajib pelunasan FA ditolak saat dikonfirmasi — ajukan sesuai sisa piutang.
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={tutupAjukan}>Batal</Button><Button onClick={submitSettle} disabled={!bukti.file_url} data-testid="rec-settle-submit">Ajukan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!writingOffId} onOpenChange={(o) => { if (!o) setWritingOffId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus-Buku Piutang?</AlertDialogTitle>
            <AlertDialogDescription>Piutang ini akan ditandai Dihapus-Buku secara permanen. Tindakan ini tidak bisa dibatalkan, dan tercatat di Audit Log.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={submitWriteOff} className="bg-neutral text-neutral-foreground hover:bg-neutral/90" data-testid="rec-writeoff-submit">Hapus-Buku</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
